import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { observer } from 'mobx-react';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import EditorToolbar from './EditorToolbar';
import QuestionCanvas from './QuestionCanvas';
import PropertiesPanel from './PropertiesPanel';
import QuestionSettingsModal from './QuestionSettingsModal';
import { JsonDefinitionModal } from './JsonDefinitionModal';
import {
    ensureDecisionBlockActions,
    ensureGroupOfBlocksActions,
    ensurePasswordTesterActions,
    ensureScoresForOptions,
    ensureYesNoListActions,
    getScoringFieldAndList,
} from './questionActionsEditor';
import './VisualEditor.scss';

function buildNumericalScaleOptions(min, max) {
    const mi = Math.max(1, Number(min) || 1);
    const ma = Math.max(mi, Number(max) || 10);
    const out = [];
    for (let i = mi; i <= ma; i++) {
        const s = String(i);
        out.push({ text: s, label: s, value: s });
    }
    return out;
}

const BLOCK_TYPES = {
    radio: {
        name: 'Radio Buttons',
        icon: '🔘',
        category: 'interactive',
        defaultProps: {
            title: 'New Radio Question',
            options: [
                { text: 'Option 1', value: 'option_1' },
                { text: 'Option 2', value: 'option_2' },
            ],
        },
    },
    checkbox: {
        name: 'Checkboxes',
        icon: '☑️',
        category: 'interactive',
        defaultProps: {
            title: 'New Checkbox Question',
            options: [
                { text: 'Option 1', value: 'option_1' },
                { text: 'Option 2', value: 'option_2' },
            ],
        },
    },
    numericalscale: {
        name: 'Numerical Scale',
        icon: '📊',
        category: 'interactive',
        defaultProps: {
            title: 'New Scale Question',
            min: 1,
            max: 5,
            options: buildNumericalScaleOptions(1, 5),
        },
    },
    'yes-no-list': {
        name: 'Yes/No List',
        icon: '✅',
        category: 'interactive',
        defaultProps: {
            title: 'New Yes/No Question',
            questions: [
                {
                    text: 'Question 1',
                    type: 'yes-no-question',
                    actionId: 'yn_row_0',
                },
                {
                    text: 'Question 2',
                    type: 'yes-no-question',
                    actionId: 'yn_row_1',
                },
            ],
        },
    },
    orderablelist: {
        name: 'Orderable List',
        icon: '📋',
        category: 'interactive',
        defaultProps: {
            title: 'New Orderable List',
            options: [
                { text: 'Item 1', value: 'item_1' },
                { text: 'Item 2', value: 'item_2' },
                { text: 'Item 3', value: 'item_3' },
            ],
        },
    },
    conversation: {
        name: 'Conversation',
        icon: '💬',
        category: 'interactive',
        defaultProps: {
            title: 'New conversation',
            pretitle: '',
            description: '',
            leftThumb: '/static/img/conversation-left.png',
            rightThumb: '/static/img/conversation-right.png',
            options: [
                {
                    id: 1,
                    side: 'left',
                    text: 'Hello',
                    value: 'hello',
                },
                {
                    id: 2,
                    side: 'right',
                    text: 'Hi there',
                    value: 'hi',
                },
            ],
        },
    },
    'password-tester': {
        name: 'Password Tester',
        icon: '🔐',
        category: 'interactive',
        defaultProps: {
            title: '',
            pretitle: '',
            description: '',
            answer: {
                heading: [
                    {
                        text: '',
                        prefix: '',
                        scoreTo: 0,
                        scoreFrom: 0,
                    },
                    {
                        text: '',
                        prefix: '',
                        scoreTo: 99,
                        scoreFrom: 1,
                    },
                    {
                        text: '',
                        prefix: '',
                        scoreTo: 100,
                        scoreFrom: 100,
                    },
                ],
                description_key: '',
                reviewHeadingText: '',
                reviewLegendStepCount: 3,
            },
        },
    },
    decision: {
        name: 'Decision Tree',
        icon: '🌳',
        category: 'interactive',
        defaultProps: {
            title: 'New Decision',
            pretitle: '',
            description: '',
            video: null,
            decisions: {},
            answer: {
                heading: [
                    {
                        text: '',
                        prefix: 'Incorrect.',
                        scoreFrom: 0,
                        scoreTo: 0,
                    },
                    {
                        text: '',
                        prefix: 'Correct.',
                        scoreFrom: 1,
                        scoreTo: 100,
                    },
                ],
            },
        },
    },
    questionwithbuttons: {
        name: 'Question with Buttons',
        icon: '🔘',
        category: 'interactive',
        defaultProps: {
            title: 'New Button Question',
            options: [
                { text: 'No', value: 'no' },
                { text: 'Yes', value: 'yes' },
            ],
        },
    },
    questionwithimage: {
        name: 'Question with Image',
        icon: '🖼️',
        category: 'interactive',
        defaultProps: {
            title: 'New Image Question',
            imageUrl: '',
        },
    },
    questionwithsocialpost: {
        name: 'Social Post Question',
        icon: '📱',
        category: 'interactive',
        defaultProps: {
            title: 'New Social Post Question',
            postData: {},
        },
    },
    groupOfBlocks: {
        name: 'Group of Blocks',
        icon: '📦',
        category: 'layout',
        defaultProps: {
            title: 'New Group',
            pretitle: '',
            description: '',
            variant: 'updates-center',
            group: [],
        },
    },
    blockHidden: {
        name: 'Hidden block',
        icon: '👁️‍🗨️',
        category: 'interactive',
        defaultProps: {
            answer: {
                heading: [
                    {
                        text: '',
                        prefix: '',
                        scoreFrom: 0,
                        scoreTo: 100,
                        description_key: '',
                    },
                ],
                reviewHeadingText: '',
            },
        },
    },
};

const PRESENTATION_TYPES = {
    illustration: {
        name: 'Illustration',
        icon: '🎨',
        category: 'presentation',
        defaultProps: {
            title: 'New Illustration',
            imageUrl: '',
            hotspots: [],
        },
    },
    'interactive-img': {
        name: 'Interactive Image',
        icon: '🖱️',
        category: 'presentation',
        defaultProps: {
            title: 'New Interactive Image',
            imageUrl: '',
            clickableAreas: [],
        },
    },
    email: {
        name: 'Email Quiz',
        icon: '📧',
        category: 'presentation',
        defaultProps: {
            title: 'New Email Quiz',
            subject: 'Test Email',
            sender: 'test@example.com',
            content: 'Email content here',
        },
    },
    message: {
        name: 'Message',
        icon: '💬',
        category: 'presentation',
        defaultProps: {
            title: 'New Message',
            content: 'Message content here',
        },
    },
    password: {
        name: 'Password',
        icon: '🔑',
        category: 'presentation',
        defaultProps: {
            title: 'New Password',
            content: 'Password content here',
        },
    },
    question: {
        name: 'Question',
        icon: '❓',
        category: 'presentation',
        defaultProps: {
            title: 'New Question',
            content: 'Question content here',
        },
    },
    video: {
        name: 'Video',
        icon: '🎥',
        category: 'presentation',
        defaultProps: {
            title: 'New Video',
            videoUrl: '',
            autoplay: false,
        },
    },
    custom: {
        name: 'Custom Presentation',
        icon: '⚙️',
        category: 'presentation',
        defaultProps: {
            title: '',
            description: '',
            content: '',
            variant: '',
            fullScreen: false,
            links: [],
        },
    },
};

function newBlockId() {
    return `block_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

/** Plain strings → { text, value } so `value` keys actions.values (see content/questions). */
function normalizeStringOptionsToObjects(options) {
    if (!Array.isArray(options)) return options;
    return options.map((opt) => {
        if (typeof opt === 'string') {
            return { text: opt, value: opt };
        }
        if (opt && typeof opt === 'object' && !Array.isArray(opt)) {
            const v =
                opt.value != null && String(opt.value) !== ''
                    ? String(opt.value)
                    : '';
            const t =
                opt.text != null
                    ? String(opt.text)
                    : opt.label != null
                      ? String(opt.label)
                      : '';
            if (!v && t) {
                return { ...opt, text: t, value: t };
            }
            if (!t && v) {
                return { ...opt, text: v, value: v };
            }
        }
        return opt;
    });
}

function normalizeBlockForEditor(block) {
    if (!block || typeof block !== 'object') return block;
    const b = { ...block };
    const withOptions = new Set([
        'radio',
        'checkbox',
        'numericalscale',
        'questionwithbuttons',
    ]);
    if (withOptions.has(b.type) && Array.isArray(b.options)) {
        b.options = normalizeStringOptionsToObjects(b.options);
    }
    if (b.type === 'questionwithbuttons' && Array.isArray(b.buttons)) {
        b.buttons = b.buttons.map((btn) =>
            typeof btn === 'string' ? { text: btn, value: btn } : btn
        );
    }
    if (b.type === 'orderablelist') {
        const hasOpts = Array.isArray(b.options) && b.options.length > 0;
        const hasItems = Array.isArray(b.items) && b.items.length > 0;
        if (!hasOpts && hasItems) {
            b.options = b.items.map((it) =>
                typeof it === 'string' ? { text: it, value: it } : it
            );
            delete b.items;
        } else if (hasOpts) {
            b.options = normalizeStringOptionsToObjects(b.options);
            if (hasItems) {
                delete b.items;
            }
        }
    }
    if (b.type === 'groupOfBlocks') {
        const hasGroup = Array.isArray(b.group) && b.group.length > 0;
        const hasBlocks = Array.isArray(b.blocks) && b.blocks.length > 0;
        if (!hasGroup && hasBlocks) {
            b.group = b.blocks.map((inner) => ({ ...inner }));
            delete b.blocks;
        }
        if (Array.isArray(b.group)) {
            b.group = b.group.map((inner) => {
                if (!inner || typeof inner !== 'object') return inner;
                const next = { ...inner };
                if (
                    (next.type === 'radio' || next.type === 'checkbox') &&
                    Array.isArray(next.options)
                ) {
                    next.options = normalizeStringOptionsToObjects(next.options);
                }
                if (next.type === 'dropDown' && Array.isArray(next.options)) {
                    next.options = normalizeStringOptionsToObjects(next.options);
                }
                return next;
            });
        }
    }
    return b;
}

function normalizeEditorBlocks(blocks) {
    const seen = new Set();
    return (blocks || []).map((b, i) => {
        let base = b.id != null && String(b.id) !== '' ? String(b.id) : '';
        if (!base) {
            const aid =
                b.actionId != null && String(b.actionId) !== ''
                    ? String(b.actionId)
                    : '';
            base = `bid_${aid || 'row'}_${i}`;
        }
        let id = base;
        let n = 0;
        while (seen.has(id)) {
            n += 1;
            id = `${base}__${n}`;
        }
        seen.add(id);
        return { ...b, id };
    });
}

function defaultQuestionState() {
    return {
        id: null,
        name: 'New Question',
        description: '',
        area: 'General',
        difficulty: 'basic',
        behaviours: [],
        languages: ['en'],
        tags: [],
        owner_unique_id: null,
        unique_id: null,
        actions: {},
        components: 'Behaviour',
        ui: {
            blocks: [],
            presentation: null,
            minimumBlockCount: 1,
        },
    };
}

function normalizeLoadedQuestion(initialQuestion) {
    const ui = initialQuestion.ui || {
        blocks: [],
        presentation: null,
        minimumBlockCount: 1,
    };
    const blocks = normalizeEditorBlocks(
        (ui.blocks || []).map(normalizeBlockForEditor)
    );
    let actions = { ...(initialQuestion.actions ?? {}) };
    const baseForActions = {
        ...initialQuestion,
        actions,
        components: initialQuestion.components ?? 'Behaviour',
        ui: {
            ...ui,
            blocks,
        },
    };
    blocks.forEach((b) => {
        const qSnap = { ...baseForActions, actions };
        if (b.type === 'password-tester' && b.actionId) {
            actions = ensurePasswordTesterActions(
                actions,
                b.actionId,
                qSnap
            );
        }
        if (b.type === 'decision' && b.actionId) {
            actions = ensureDecisionBlockActions(actions, b, qSnap);
        }
        if (b.type === 'groupOfBlocks' && b.actionId) {
            actions = ensureGroupOfBlocksActions(actions, b, qSnap);
        }
    });
    return {
        ...initialQuestion,
        actions,
        components: initialQuestion.components ?? 'Behaviour',
        ui: {
            blocks,
            presentation: ui.presentation ?? null,
            minimumBlockCount: ui.minimumBlockCount ?? 1,
        },
    };
}

function VisualEditor({
    initialQuestion = null,
    onSave,
    onSaveAs,
    onCancel,
    companyId,
    companyUid,
    accountUid,
    /** Primary colour for step indicators (optional). */
    primaryColor,
    /**
     * Render learner preview: ({ question, companyId, onClose }) => ReactNode
     * Host app supplies LMS AssessmentQuestionPreview modal, etc.
     */
    renderPreviewModal,
    /**
     * Company threat areas from admin (e.g. LMS `catalogStore.availableThreatAreas`).
     * Each item: `{ threatAreaName, ... }` or a string name.
     */
    threatAreaOptions,
    /**
     * Full threat area records with `keyBehaviours` (e.g. `catalogStore.threatAreaServerData`).
     */
    threatAreasCatalog,
}) {
    const [question, setQuestion] = useState(() =>
        initialQuestion
            ? normalizeLoadedQuestion(initialQuestion)
            : defaultQuestionState()
    );

    const [selectedBlockId, setSelectedBlockId] = useState(null);
    const [presentationSelected, setPresentationSelected] = useState(false);
    const [showPreview, setShowPreview] = useState(false);
    const [showJsonDefinition, setShowJsonDefinition] = useState(false);
    const [questionSettingsOpen, setQuestionSettingsOpen] = useState(false);
    const [isDirty, setIsDirty] = useState(false);

    const selectedBlock = useMemo(
        () =>
            question.ui.blocks.find((b) => b.id === selectedBlockId) ?? null,
        [question.ui.blocks, selectedBlockId]
    );

    useEffect(() => {
        if (initialQuestion) {
            setQuestion(normalizeLoadedQuestion(initialQuestion));
            setSelectedBlockId(null);
            setPresentationSelected(false);
            setIsDirty(false);
        } else {
            setQuestion(defaultQuestionState());
            setSelectedBlockId(null);
            setPresentationSelected(false);
            setIsDirty(false);
        }
    }, [initialQuestion]);

    const canSaveInPlace = useMemo(() => {
        if (!question.id) return true;
        const owner = question.owner_unique_id;
        if (!owner) return false;
        if (companyUid && owner === companyUid) return true;
        if (accountUid && owner === accountUid) return true;
        return false;
    }, [
        question.id,
        question.owner_unique_id,
        companyUid,
        accountUid,
    ]);

    const updateQuestion = useCallback((updates) => {
        setQuestion((prev) => ({ ...prev, ...updates }));
        setIsDirty(true);
    }, []);

    const patchQuestion = useCallback((recipe) => {
        setQuestion((prev) => recipe(prev));
        setIsDirty(true);
    }, []);

    const addBlock = useCallback((blockType, position = -1) => {
        const blockConfig = BLOCK_TYPES[blockType];
        if (!blockConfig) return;

        let newBlock = {
            id: newBlockId(),
            type: blockType,
            actionId: `action_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
            ...blockConfig.defaultProps,
        };

        if (blockType === 'yes-no-list') {
            const stamp = Date.now();
            newBlock = {
                ...newBlock,
                questions: (newBlock.questions || []).map((q, i) => ({
                    ...q,
                    type: 'yes-no-question',
                    actionId:
                        q.actionId && String(q.actionId)
                            ? q.actionId
                            : `yn_${stamp}_${i}`,
                })),
            };
        }

        if (blockType === 'decision') {
            const sid = `step_${Date.now()}`;
            newBlock = {
                ...newBlock,
                decisions: {
                    [sid]: {
                        order: 1,
                        question: 'What should you do?',
                        options: [
                            { text: 'Option A', value: 'opt_a' },
                            { text: 'Option B', value: 'opt_b' },
                        ],
                    },
                },
                answer: newBlock.answer || {
                    heading: [
                        {
                            text: '',
                            prefix: 'Incorrect.',
                            scoreFrom: 0,
                            scoreTo: 0,
                        },
                        {
                            text: '',
                            prefix: 'Correct.',
                            scoreFrom: 1,
                            scoreTo: 100,
                        },
                    ],
                },
            };
        }

        if (blockType === 'groupOfBlocks') {
            const t = Date.now();
            const c1 = `grp_${t}_a`;
            const c2 = `grp_${t}_b`;
            newBlock = {
                ...newBlock,
                variant: newBlock.variant || 'updates-center',
                group: [
                    {
                        type: 'radio',
                        title: 'Question 1',
                        actionId: c1,
                        options: [
                            { text: 'Yes', value: 'yes' },
                            { text: 'No', value: 'no' },
                        ],
                    },
                    {
                        type: 'radio',
                        title: 'Question 2',
                        actionId: c2,
                        options: [
                            { text: 'Choice A', value: 'a' },
                            { text: 'Choice B', value: 'b' },
                        ],
                    },
                ],
            };
        }

        const flatScoringTypes = new Set([
            'radio',
            'checkbox',
            'conversation',
            'numericalscale',
            'orderablelist',
            'questionwithbuttons',
        ]);

        setQuestion((prev) => {
            const newBlocks = [...prev.ui.blocks];
            if (position === -1) {
                newBlocks.push(newBlock);
            } else {
                newBlocks.splice(position, 0, newBlock);
            }
            let nextActions = { ...(prev.actions || {}) };
            if (flatScoringTypes.has(blockType)) {
                const spec = getScoringFieldAndList(newBlock);
                if (spec?.list?.length) {
                    nextActions = ensureScoresForOptions(
                        nextActions,
                        newBlock.actionId,
                        spec.list,
                        prev
                    );
                }
            }
            if (blockType === 'yes-no-list') {
                nextActions = ensureYesNoListActions(
                    nextActions,
                    newBlock,
                    prev
                );
            }
            if (blockType === 'password-tester') {
                nextActions = ensurePasswordTesterActions(
                    nextActions,
                    newBlock.actionId,
                    prev
                );
            }
            if (blockType === 'decision') {
                nextActions = ensureDecisionBlockActions(
                    nextActions,
                    newBlock,
                    prev
                );
            }
            if (blockType === 'groupOfBlocks') {
                nextActions = ensureGroupOfBlocksActions(
                    nextActions,
                    newBlock,
                    prev
                );
            }
            return {
                ...prev,
                actions: nextActions,
                ui: {
                    ...prev.ui,
                    blocks: newBlocks,
                },
            };
        });
        setIsDirty(true);
    }, []);

    const removeBlock = useCallback((blockId) => {
        if (blockId == null || blockId === '') return;
        setQuestion((prev) => {
            const removed = prev.ui.blocks.find((b) => b.id === blockId);
            const newBlocks = prev.ui.blocks.filter(
                (block) => block.id !== blockId
            );
            const actions = { ...(prev.actions || {}) };
            if (removed?.actionId) {
                delete actions[removed.actionId];
            }
            return {
                ...prev,
                actions,
                ui: {
                    ...prev.ui,
                    blocks: newBlocks,
                },
            };
        });
        setIsDirty(true);
        setSelectedBlockId((id) => (id === blockId ? null : id));
    }, []);

    const updateBlock = useCallback((blockId, updates) => {
        setQuestion((prev) => {
            const newBlocks = prev.ui.blocks.map((block) =>
                block.id === blockId ? { ...block, ...updates } : block
            );
            return {
                ...prev,
                ui: {
                    ...prev.ui,
                    blocks: newBlocks,
                },
            };
        });
        setIsDirty(true);
    }, []);

    const updateSelectedBlockProps = useCallback(
        (updates) => {
            if (!selectedBlock) return;
            updateBlock(selectedBlock.id, updates);
        },
        [selectedBlock, updateBlock]
    );

    const setPresentation = useCallback((presentationType) => {
        const presentationConfig = PRESENTATION_TYPES[presentationType];
        if (!presentationConfig) return;

        const newPresentation = {
            type: presentationType,
            ...presentationConfig.defaultProps,
        };

        setQuestion((prev) => ({
            ...prev,
            ui: {
                ...prev.ui,
                presentation: newPresentation,
            },
        }));
        setIsDirty(true);
        setSelectedBlockId(null);
    }, []);

    const updatePresentation = useCallback((updates) => {
        setQuestion((prev) => {
            const current = prev.ui.presentation;
            if (!current) return prev;
            return {
                ...prev,
                ui: {
                    ...prev.ui,
                    presentation: { ...current, ...updates },
                },
            };
        });
        setIsDirty(true);
    }, []);

    const changePresentationType = useCallback(
        (presentationType) => {
            if (question.ui.presentation?.type === presentationType) {
                return;
            }
            setPresentation(presentationType);
        },
        [question.ui.presentation?.type, setPresentation]
    );

    const handleSave = useCallback(async () => {
        try {
            if (onSave) {
                await onSave(question);
            }
            setIsDirty(false);
        } catch (error) {
            void error;
        }
    }, [question, onSave]);

    const handleSaveAs = useCallback(async () => {
        if (!onSaveAs) return;
        try {
            await onSaveAs(question);
            setIsDirty(false);
        } catch (error) {
            void error;
        }
    }, [question, onSaveAs]);

    const handlePreview = useCallback(() => {
        if (!renderPreviewModal) return;
        setShowPreview(true);
    }, [renderPreviewModal]);

    const previewQuestion = useMemo(() => {
        const ui =
            question.ui || {
                blocks: [],
                presentation: null,
                minimumBlockCount: 1,
            };
        const id = question.id || 'preview-draft';
        return {
            ...question,
            id,
            ui,
            definition: {
                ...(question.definition || {}),
                ui,
            },
        };
    }, [question]);

    const drawerOpen = Boolean(selectedBlockId || presentationSelected);
    const drawerPresentation = presentationSelected
        ? question.ui.presentation
        : null;

    return (
        <div className="visual-editor">
            <EditorToolbar
                question={question}
                isDirty={isDirty}
                canSaveInPlace={canSaveInPlace}
                onSave={handleSave}
                onSaveAs={onSaveAs ? handleSaveAs : undefined}
                onPreview={renderPreviewModal ? handlePreview : undefined}
                onShowJson={() => setShowJsonDefinition(true)}
                onQuestionSettings={() => setQuestionSettingsOpen(true)}
                onCancel={onCancel}
            />

            <div className="visual-editor-content">
                <div className="visual-editor-center">
                    <QuestionCanvas
                        question={question}
                        blockTypes={BLOCK_TYPES}
                        presentationTypes={PRESENTATION_TYPES}
                        selectedBlockId={selectedBlockId}
                        presentationSelected={presentationSelected}
                        onSelectBlockId={(id) => {
                            setSelectedBlockId(id);
                            setPresentationSelected(false);
                        }}
                        onSelectPresentation={() => {
                            setPresentationSelected(true);
                            setSelectedBlockId(null);
                        }}
                        onRemoveBlock={removeBlock}
                        onAddBlock={addBlock}
                        onSetPresentation={setPresentation}
                        primaryColor={primaryColor}
                    />
                </div>
            </div>

            <Drawer
                anchor="right"
                open={drawerOpen}
                onClose={() => {
                    setSelectedBlockId(null);
                    setPresentationSelected(false);
                }}
                PaperProps={{
                    className: 'visual-editor-properties-drawer',
                    sx: {
                        width: { xs: '100%', sm: 540 },
                        maxWidth: '100%',
                        boxSizing: 'border-box',
                    },
                }}
            >
                <Box
                    sx={{
                        p: 2,
                        width: { xs: '100%', sm: 540 },
                        maxWidth: '100%',
                        boxSizing: 'border-box',
                    }}
                >
                    <PropertiesPanel
                        variant="selection-only"
                        selectedBlock={selectedBlock}
                        selectedPresentation={drawerPresentation}
                        question={question}
                        threatAreaOptions={threatAreaOptions}
                        threatAreasCatalog={threatAreasCatalog}
                        presentationTypes={PRESENTATION_TYPES}
                        onChangePresentationType={changePresentationType}
                        onPatchQuestion={patchQuestion}
                        onUpdateBlock={updateSelectedBlockProps}
                        onUpdatePresentation={updatePresentation}
                        onUpdateQuestion={updateQuestion}
                    />
                </Box>
            </Drawer>

            <QuestionSettingsModal
                open={questionSettingsOpen}
                question={question}
                threatAreaOptions={threatAreaOptions}
                threatAreasCatalog={threatAreasCatalog}
                onClose={() => setQuestionSettingsOpen(false)}
                onUpdateQuestion={(updates) => {
                    updateQuestion(updates);
                }}
            />

            {showPreview && renderPreviewModal
                ? renderPreviewModal({
                      question: previewQuestion,
                      companyId,
                      onClose: () => setShowPreview(false),
                  })
                : null}

            <JsonDefinitionModal
                open={showJsonDefinition}
                question={question}
                onClose={() => setShowJsonDefinition(false)}
            />
        </div>
    );
}

export default observer(VisualEditor);
